/**
 * Leitura da ficha do cliente (redesign 29/10/2026): indicadores do hero,
 * barra de ambientes por status, datas-chave, pessoas, filtros por status,
 * diferença da reprogramação e a linha do tempo. Funções puras sobre o
 * cliente montado por `montarClientes`.
 */
import { formatarData } from "../../../../utils/calculos";
import { hasValue } from "../../../../utils/data";
import { F } from "../../../../utils/gerencialFields";
import { toDate } from "../../../../utils/status";
import { compararTexto } from "./montarClientes";
import { diasAte, estaAtrasado, faixaDeSaude, prazoRelativo, tomDoPrazo } from "./regras";

const STATUS_ENTREGUE = "Entregue";
const MS_POR_DIA = 864e5;

/** "Aguardando Compras" → "Aguard. compras"; nomes curtos para pílulas e filtros. */
const CURTOS = {
  "Aguardando Início de Marcenaria": "Aguard. marcenaria",
  "Aguardando Finalizar Marcenaria": "Finalizando marcenaria",
  "Aguardando Fornecedores Terceiros": "Aguard. terceiros",
  "Aguardando Otimização de Lote": "Aguard. otimização",
  "Aguardando Projeto Executivo": "Aguard. executivo",
  Entregue: "Entregue",
};

export function statusCurto(status) {
  if (CURTOS[status]) return CURTOS[status];
  const texto = String(status || "—");
  if (!texto.startsWith("Aguardando ")) return texto;
  const resto = texto.slice("Aguardando ".length);
  return `Aguard. ${resto.charAt(0).toLowerCase()}${resto.slice(1)}`;
}

/** Tom da pílula a partir do `statusVariant` do legado. */
const TOM_DA_VARIANTE = {
  success: "ok",
  lime: "acc",
  warning: "warn",
  orange: "warn",
  danger: "bad",
  info: "acc",
  today: "acc",
  purple: "vio",
};
export function tomDaVariante(variante) {
  return TOM_DA_VARIANTE[variante] || "neu";
}

/** Tom do status: "Entregue" é verde no redesign (no legado era neutro); o resto segue a variante. */
export function tomDoStatus(status, variante) {
  return status === STATUS_ENTREGUE ? "ok" : tomDaVariante(variante);
}

/** Revisões da planilha são datas: quantas e a última. Texto que não é data vai como veio. */
export function resumoDasRevisoes(revisoes) {
  const datas = (revisoes || []).map((r) => toDate(r)).filter(Boolean).sort((a, b) => a - b);
  if (!datas.length) return { texto: (revisoes || []).join(", ") || "—", ultima: "" };
  return { texto: plural(datas.length, "revisão", "revisões"), ultima: formatarData(datas[datas.length - 1]) };
}

/** Cor da fatia na barra do hero (mesmo tom da pílula). */
const COR_DO_TOM = { ok: "var(--ok)", warn: "var(--warn)", bad: "var(--bad)", acc: "var(--accent)", vio: "var(--c2)", neu: "var(--neutral-solid)" };
export function corDoTom(tom) {
  return COR_DO_TOM[tom] || COR_DO_TOM.neu;
}

export function iniciais(nome) {
  return String(nome || "").split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

/** Diferença da reprogramação contra a entrega prevista (dias); null se não houve. */
export function diferencaDaReprogramacao(ambiente) {
  const prevista = toDate(ambiente.entregaContrato);
  const reprogramada = toDate(ambiente.dataReprogramada);
  if (!prevista || !reprogramada) return null;
  const dias = Math.round((reprogramada.setHours(0, 0, 0, 0) - prevista.setHours(0, 0, 0, 0)) / MS_POR_DIA);
  return dias === 0 ? null : dias;
}

function ambientesDo(cliente) {
  return cliente.pedidos.flatMap((p) => p.ambientes);
}

/** Pendências do legado (terceiros/acabamentos) de ambientes já entregues. */
function entreguesComPendencia(cliente) {
  return ambientesDo(cliente).filter((a) => a.status === STATUS_ENTREGUE && (a.pendencias || []).length > 0).length;
}

/** Ambientes em atraso: não entregues com prazo vencido ou com atraso no contrato. */
export function ambienteEmAtraso(ambiente, hoje = new Date()) {
  if (ambiente.status === STATUS_ENTREGUE) return false;
  const dias = diasAte(ambiente.dataReprogramada || ambiente.entregaContrato, hoje);
  return (dias != null && dias < 0) || ambiente.contractDelayDays > 0;
}

/** Fatias da barra do hero: um segmento por status (ordem do fluxo), com cor e total. */
export function fatiasDosAmbientes(cliente) {
  const porStatus = new Map();
  ambientesDo(cliente).forEach((a) => {
    const atual = porStatus.get(a.status) || { status: a.status, total: 0, tom: tomDoStatus(a.status, a.statusVariant) };
    atual.total += 1;
    porStatus.set(a.status, atual);
  });
  const ordem = cliente.pedidos[0]?.statusCounts?.map((s) => s.label) || [];
  return [...porStatus.values()]
    .sort((a, b) => (a.status === STATUS_ENTREGUE ? -1 : b.status === STATUS_ENTREGUE ? 1 : ordem.indexOf(a.status) - ordem.indexOf(b.status)))
    .map((f) => ({ ...f, rotulo: f.status, cor: corDoTom(f.tom) }));
}

function maisComum(valores) {
  const contagem = new Map();
  valores.forEach((v) => contagem.set(v, (contagem.get(v) || 0) + 1));
  return [...contagem.entries()].sort((a, b) => b[1] - a[1])[0] || null;
}

function plural(n, um, varios) {
  return `${n} ${n === 1 ? um : varios}`;
}

/** Os seis indicadores do hero. */
export function indicadoresDoHero(cliente, hoje = new Date()) {
  const ambientes = ambientesDo(cliente);
  const emAndamento = ambientes.filter((a) => a.status !== STATUS_ENTREGUE);
  const principal = maisComum(emAndamento.map((a) => a.status));
  const dias = diasAte(cliente.proximaEntrega, hoje);
  const atrasado = estaAtrasado({ diasProximaEntrega: dias, atrasoAcumulado: cliente.totalDelayDays });
  const faixa = faixaDeSaude({ saude: cliente.healthPercentage, atrasado, atrasoAcumulado: cliente.totalDelayDays });
  const pedidosComVencidos = cliente.pedidos.filter((p) => p.totalDelayDays > 0).length;
  return {
    faixa,
    saude: cliente.healthPercentage,
    saudeLegenda: cliente.delayedAmbientesCount
      ? `${plural(cliente.delayedAmbientesCount, "ambiente", "ambientes")} com atraso no contrato`
      : "nenhum ambiente atrasado",
    emAndamento: emAndamento.length,
    emAndamentoLegenda: principal ? `${principal[1]} ${statusCurto(principal[0]).toLowerCase()}` : "tudo entregue",
    entregues: cliente.totalEntregues,
    total: cliente.totalAmbientes,
    entreguesLegenda: cliente.ultimaEntrega ? `última em ${formatarData(cliente.ultimaEntrega).slice(0, 5)}` : "nenhuma entrega ainda",
    atraso: cliente.totalDelayDays,
    atrasoLegenda: pedidosComVencidos ? `${plural(pedidosComVencidos, "pedido", "pedidos")} com vencidos` : "nenhum pedido vencido",
    aguardandoLiberacao: cliente.totalAmbientesAguardandoLiberacao,
    liberacaoLegenda: cliente.totalAmbientesAguardandoLiberacao ? "esperando liberar a obra" : "obra liberada",
    valor: cliente.valor,
    valorLegenda: plural(cliente.totalPedidos, "pedido", "pedidos"),
    comPendenciaDeTerceiros: entreguesComPendencia(cliente),
    proximaEntrega: cliente.proximaEntrega,
    diasProximaEntrega: dias,
  };
}

/** Datas-chave da coluna de contexto. */
export function datasChave(cliente, hoje = new Date()) {
  const vendas = [...cliente.pedidos]
    .filter((p) => p.dataVenda)
    .sort((a, b) => toDate(a.dataVenda) - toDate(b.dataVenda));
  const itens = [];
  if (cliente.primeiraVenda) itens.push({ rotulo: "Primeira venda", valor: formatarData(cliente.primeiraVenda) });
  vendas.slice(1).forEach((p) => itens.push({ rotulo: `Venda ${p.numero || p.label}`, valor: formatarData(p.dataVenda) }));
  if (cliente.ultimaEntrega) itens.push({ rotulo: "Última entrega", valor: formatarData(cliente.ultimaEntrega) });
  if (cliente.proximaEntrega) {
    const dias = diasAte(cliente.proximaEntrega, hoje);
    itens.push({
      rotulo: "Próxima entrega",
      valor: `${formatarData(cliente.proximaEntrega)} · ${prazoRelativo(dias)}`,
      tom: tomDoPrazo(dias),
    });
  }
  return itens;
}

/** Pessoas do cliente na planilha: vendedores, consultores (RC) e marceneiros. */
export function pessoasDoCliente(cliente, projetistas = []) {
  const valido = (n) => hasValue(n) && n !== "—";
  return {
    equipe: [
      ...cliente.vendedores.filter(valido).map((nome) => ({ nome, papel: "vendas" })),
      ...cliente.consultores.filter(valido).map((nome) => ({ nome, papel: "RC" })),
      ...projetistas.filter(valido).map((nome) => ({ nome, papel: "projeto" })),
    ],
    marceneiros: cliente.marceneiros.filter(valido).sort(compararTexto).map((nome) => ({
      nome: nome.replace(/\s*\(empreita\)\s*/i, ""),
      papel: /\(empreita\)/i.test(nome) ? "empreita" : "",
    })),
  };
}

/** Pedido "extra" é o que leva sufixo (1178-1) ou o rótulo da planilha diz EXTRA. */
export function pedidoExtra(pedido) {
  return /-\d+$/.test(String(pedido.numero || "")) || /extra/i.test(String(pedido.label || ""));
}

/** Filtros por status da aba Ambientes, na ordem do fluxo, com "Em atraso" à frente quando houver. */
export function filtrosDeStatus(cliente, hoje = new Date()) {
  const contagem = new Map();
  ambientesDo(cliente).forEach((a) => {
    [a.status, ...(a.pendencias || []).map((p) => p.status)].forEach((s) => contagem.set(s, (contagem.get(s) || 0) + 1));
  });
  const ordem = fatiasDosAmbientes(cliente).map((f) => f.status);
  const extras = [...contagem.keys()].filter((s) => !ordem.includes(s));
  const filtros = [...ordem.filter((s) => s !== STATUS_ENTREGUE), ...extras, ...(contagem.has(STATUS_ENTREGUE) ? [STATUS_ENTREGUE] : [])]
    .map((status) => ({ id: status, rotulo: status === STATUS_ENTREGUE ? "Entregues" : statusCurto(status), total: contagem.get(status) }));
  const atrasados = ambientesDo(cliente).filter((a) => ambienteEmAtraso(a, hoje)).length;
  return atrasados ? [{ id: "atraso", rotulo: "Em atraso", total: atrasados, tom: "bad" }, ...filtros] : filtros;
}

export function filtrarAmbientes(ambientes, { busca = "", status = "" }, hoje = new Date()) {
  const termo = String(busca).trim().toLowerCase();
  return ambientes.filter((a) => {
    if (status === "atraso" && !ambienteEmAtraso(a, hoje)) return false;
    if (status && status !== "atraso" && a.status !== status && !(a.pendencias || []).some((p) => p.status === status)) return false;
    if (!termo) return true;
    return [a.ambiente, a.lote, a.marceneiro, a.status, ...(a.pendencias || []).map((p) => p.status), a.row?.[F.OBSERVACOES_GERAIS]]
      .filter(Boolean).join(" ").toLowerCase().includes(termo);
  });
}

// ------------------------------------------------------------
// Linha do tempo do cliente (planilha + comentários)
// ------------------------------------------------------------

function porDia(ambientes, campo) {
  const grupos = new Map();
  ambientes.forEach((a) => {
    const d = toDate(a.row?.[campo]);
    if (!d) return;
    const chave = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const grupo = grupos.get(chave) || { data: d, nomes: [] };
    grupo.nomes.push(a.ambiente);
    grupos.set(chave, grupo);
  });
  return [...grupos.values()];
}

function listaCurta(nomes) {
  return nomes.length <= 3 ? nomes.join(", ") : `${nomes.slice(0, 3).join(", ")} e mais ${nomes.length - 3}`;
}

/**
 * Eventos do cliente, do mais novo para o mais antigo: venda de cada pedido,
 * liberação da obra, entregas (carregamento) e comentários do histórico.
 */
export function linhaDoTempo(cliente, comentarios = []) {
  const eventos = [];
  cliente.pedidos.forEach((p) => {
    const rotulo = `Pedido ${p.numero || p.label}`;
    if (p.dataVenda) eventos.push({ data: toDate(p.dataVenda), titulo: `${rotulo} vendido`, detalhe: plural(p.totalAmbientes, "ambiente", "ambientes"), cor: "var(--accent)" });
    porDia(p.ambientes, F.LIBERACAO_DA_OBRA).forEach((g) => eventos.push({
      data: g.data, titulo: `Obra liberada · ${rotulo}`, detalhe: listaCurta(g.nomes), cor: "var(--neutral-solid)",
    }));
    porDia(p.ambientes, F.CARREGAMENTO).forEach((g) => eventos.push({
      data: g.data, titulo: `${plural(g.nomes.length, "ambiente entregue", "ambientes entregues")} · ${rotulo}`,
      detalhe: listaCurta(g.nomes), cor: "var(--ok)",
    }));
  });
  comentarios.forEach((c) => eventos.push({
    data: new Date(c.criado_em), titulo: `Comentário em ${c.rotulo || c.chave}`, detalhe: `${c.autor} — ${c.texto}`, cor: "var(--c2)",
  }));
  return eventos
    .filter((e) => e.data && !Number.isNaN(e.data.getTime()))
    .sort((a, b) => b.data - a.data)
    .map((e, i) => ({ ...e, id: `${i}-${e.titulo}`, dataLabel: formatarData(e.data) }));
}

// ------------------------------------------------------------
// Próxima ação da ficha
// ------------------------------------------------------------

const ATRASO_QUE_PEDE_REPLANEJAR = 30;

function prazoDoAmbiente(a) {
  return a.dataReprogramada || a.entregaContrato || null;
}

/** Ambientes não entregues que vencem no mesmo dia da próxima entrega do cliente. */
function ambientesDaProximaEntrega(cliente) {
  const alvo = toDate(cliente.proximaEntrega);
  if (!alvo) return [];
  return cliente.pedidos.flatMap((p) => p.ambientes.map((a) => ({ ...a, pedido: p.numero || p.label })))
    .filter((a) => a.status !== STATUS_ENTREGUE)
    .filter((a) => {
      const d = toDate(prazoDoAmbiente(a));
      return d && d.toDateString() === alvo.toDateString();
    });
}

function descreverVencimento(ambientes, verbo) {
  if (!ambientes.length) return "";
  const pedidos = [...new Set(ambientes.map((a) => a.pedido))];
  const deQuem = pedidos.length === 1 ? `do pedido ${pedidos[0]}` : `de ${pedidos.length} pedidos`;
  return `${plural(ambientes.length, "ambiente", "ambientes")} ${deQuem} ${verbo}: ${listaCurta(ambientes.map((a) => a.ambiente))}.`;
}

/**
 * Próxima ação do cliente, na prioridade do negócio (a mesma da lista):
 * entrega vencida → atraso que pede replanejar → entrega em até 7 dias →
 * cliente esperando com pendências → cobrar o cliente → liberação → em dia.
 * `acao` diz o que o botão faz: filtrar atrasados, abrir pendências etc.
 */
export function proximaAcao(cliente, { espera = null, pendencias = 0 } = {}, hoje = new Date()) {
  const dias = diasAte(cliente.proximaEntrega, hoje);
  const vencendo = ambientesDaProximaEntrega(cliente);
  const data = cliente.proximaEntrega ? formatarData(cliente.proximaEntrega).slice(0, 5) : "";
  if (dias != null && dias < 0) {
    return { tom: "bad", quando: prazoRelativo(dias), titulo: "Confirmar nova data com o cliente",
      descricao: descreverVencimento(vencendo, `venceram em ${data}`), botao: "Ver itens em atraso", acao: "atraso" };
  }
  if (cliente.totalDelayDays > ATRASO_QUE_PEDE_REPLANEJAR) {
    return { tom: "bad", quando: `${cliente.totalDelayDays} d acumulados`, titulo: "Replanejar os ambientes vencidos",
      descricao: `${plural(cliente.delayedAmbientesCount, "ambiente passou", "ambientes passaram")} da data do contrato.`,
      botao: "Ver itens em atraso", acao: "atraso" };
  }
  if (dias != null && dias <= 7 && cliente.totalEmAndamento > 0) {
    return { tom: dias <= 1 ? "warn" : "acc", quando: prazoRelativo(dias), titulo: `Confirmar a entrega de ${data} com o cliente`,
      descricao: descreverVencimento(vencendo, dias === 0 ? "vencem hoje" : `vencem ${prazoRelativo(dias)}`),
      botao: "Ver ambientes", acao: "ambientes" };
  }
  if (espera === "equipe" && pendencias > 0) {
    return { tom: "warn", quando: "o cliente espera", titulo: `Responder ${plural(pendencias, "pendência aberta", "pendências abertas")}`,
      descricao: "O cliente está esperando um retorno da equipe.", botao: "Ver pendências", acao: "pendencias" };
  }
  if (espera === "cliente") {
    return { tom: "neu", quando: "a equipe espera", titulo: "Cobrar retorno do cliente",
      descricao: "O andamento depende de uma resposta do cliente.", botao: null, acao: null };
  }
  if (cliente.totalAmbientesAguardandoLiberacao > 0) {
    return { tom: "warn", quando: "liberação", titulo: `${plural(cliente.totalAmbientesAguardandoLiberacao, "ambiente aguarda", "ambientes aguardam")} liberação`,
      descricao: "Sem a liberação da obra, esses ambientes não avançam.", botao: "Ver ambientes", acao: "liberacao" };
  }
  if (cliente.totalEmAndamento === 0 && cliente.totalAmbientes > 0) {
    return { tom: "ok", quando: "concluído", titulo: "Todos os ambientes entregues", descricao: "Nada pendente na planilha.", botao: null, acao: null };
  }
  return { tom: "neu", quando: data ? `entrega ${data}` : "", titulo: "Acompanhar os ambientes em andamento",
    descricao: data ? `Próxima entrega ${prazoRelativo(dias)}.` : "Sem data de entrega na planilha.", botao: null, acao: null };
}
